/**
 * Client for the platform-admin runtime workspace browser
 * (`GET /api/platform/runtimes/:userId/fs/{list,read}`).
 *
 * Separate from `PlatformApi` on purpose: those endpoints PROXY the
 * runtime plugin's raw response (`{root,path,folders,files}` /
 * `{...content...}`), which is NOT wrapped in lexserver's `{ok,data}`
 * envelope — so `PlatformApi.request()`'s envelope-unwrap would reject
 * it. This thin client returns the body verbatim. Auth uses the same
 * cached Lexiun (platform-admin) JWT + W3C traceparent as PlatformApi.
 */

import { getLexiunToken } from "./lexiun-jwt";
import { newTraceparent } from "./traceparent";

const LEXSERVER_BASE = "/lexserver";

export type FsRoot = "org" | "agent";

export interface FsEntry {
  name: string;
  /** Root-relative path ("" = the root). */
  path: string;
  isDirectory: boolean;
  size?: number;
  modifiedAt?: string;
  isSymlink?: boolean;
}

export interface FsListing {
  root: FsRoot;
  path: string;
  folders: FsEntry[];
  files: FsEntry[];
}

export interface FsReadResult {
  root: FsRoot;
  path: string;
  content: string;
  encoding: "utf8" | "base64";
  size: number;
  truncated: boolean;
}

export class FsBrowserError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
    this.name = "FsBrowserError";
  }
}

async function fsRequest<T>(path: string): Promise<T> {
  const token = await getLexiunToken();
  const headers: Record<string, string> = { traceparent: newTraceparent() };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${LEXSERVER_BASE}${path}`, { headers });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new FsBrowserError(body.error ?? "FS_ERROR", body.message ?? res.statusText, res.status);
  }
  return res.json() as Promise<T>;
}

export function listRuntimeFs(userId: string, root: FsRoot, path: string): Promise<FsListing> {
  const qs = new URLSearchParams({ root, path });
  return fsRequest<FsListing>(`/api/platform/runtimes/${encodeURIComponent(userId)}/fs/list?${qs}`);
}

export function readRuntimeFs(userId: string, root: FsRoot, path: string): Promise<FsReadResult> {
  const qs = new URLSearchParams({ root, path });
  return fsRequest<FsReadResult>(`/api/platform/runtimes/${encodeURIComponent(userId)}/fs/read?${qs}`);
}
